import React from 'react';
import { Link } from 'expo-router';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useHydratedWindowDimensions } from '../../hooks/useHydratedWindowDimensions';
import colors from '../../lib/colors';
import { interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import type { PublicDigestData } from '../../lib/usePublicHomeDigests';
import {
    Body,
    Heading,
    PrimaryButton,
    Section,
    SectionLabel,
    StepCard,
} from '../about/AboutPrimitives';
import SystemDiagram from '../about/SystemDiagram';
import HomeDigestExamples from './HomeDigestExamples';
import HomeFAQ from './HomeFAQ';
import HomeResearchBrief from './HomeResearchBrief';
import HomeSourceSection from './HomeSourceSection';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import type { MessageDescriptor } from '@lingui/core';

interface HomePageBodyProps {
    digests: PublicDigestData[] | null;
    fontsLoaded: boolean;
    onOpenChat: () => void;
    targetCount: number;
}

const AUDIENCE_COLUMNS = [
    {
        label: msg({ id: "home.homepagebody.humanReview", message: "Human review", comment: "UI label in components/home/HomePageBody.tsx" }),
        title: msg({ id: "home.homepagebody.forResearchersAndTeams", message: "For researchers and teams", comment: "UI title in components/home/HomePageBody.tsx" }),
        body: msg({ id: "home.homepagebody.skimTheAppFeedGetEmailDigestsRssOrAMorningBriefFromYourAiAssistant", message: "Skim the app feed, receive daily or weekly email digests, subscribe through RSS, or get a morning brief from your AI assistant. Stay current on narrow research topics without living in alerts, search pages, and source-specific inboxes.", comment: "UI body in components/home/HomePageBody.tsx" }),
        featuredLink: {
            label: msg({ id: 'home.audience.morningBrief', message: 'Morning brief in your AI assistant →' }),
            href: '/research-briefs' as const,
        },
        bullets: [
            msg({ id: 'home.audience.inAppFeed', message: 'In-app research feed' }),
            msg({ id: 'home.audience.emailDigest', message: 'Daily or weekly email digest' }),
            msg({ id: 'home.audience.rssFeed', message: 'RSS/Atom feed' }),
            msg({ id: 'home.audience.topicMonitoring', message: 'Topic-specific monitoring' }),
            msg({ id: 'home.audience.triage', message: 'Must-read and related-source triage' }),
        ],
        dark: false,
    },
    {
        label: msg({ id: "home.homepagebody.agentWorkflows", message: "Agent workflows", comment: "UI label in components/home/HomePageBody.tsx" }),
        title: msg({ id: "home.homepagebody.forAiAgentsAndAutomation", message: "For AI agents and automation", comment: "UI title in components/home/HomePageBody.tsx" }),
        href: '/agents' as const,
        body: msg({ id: "home.homepagebody.giveResearchAgentsACurrentScopedResearchDataStreamInsteadOfAskingThemToRediscove", message: "Give research agents a current, scoped research-data stream instead of asking them to rediscover the field from scratch. MCP, CLI, and API access make Paperzilla output usable inside agent workflows, scripts, and recurring research briefs.", comment: "UI body in components/home/HomePageBody.tsx" }),
        bullets: [
            msg({ id: 'home.audience.mcpAccess', message: 'MCP server access' }),
            msg({ id: 'home.audience.cliAccess', message: 'CLI access' }),
            msg({ id: 'home.audience.agentStreams', message: 'Agent-ready research streams' }),
            msg({ id: 'home.audience.structuredContext', message: 'Structured research context' }),
            msg({ id: 'home.audience.recurringBriefs', message: 'Recurring briefing workflows' }),
        ],
        dark: true,
    },
];

const USE_CASES = [
    {
        title: msg({ id: "home.homepagebody.researchers", message: "Researchers", comment: "UI title in components/home/HomePageBody.tsx" }),
        body: msg({ id: "home.homepagebody.trackANarrowTopicWithoutCheckingEverySourceAlertAndNewsletterManuallySeeThePaper", message: "Track a narrow topic without checking every source, alert, and newsletter manually. See the papers, datasets, repositories, and patents most likely to matter before they disappear into the pile.", comment: "UI body in components/home/HomePageBody.tsx" }),
    },
    {
        title: msg({ id: "home.homepagebody.labsAndResearchTeams", message: "Labs and research teams", comment: "UI title in components/home/HomePageBody.tsx" }),
        body: msg({ id: "home.homepagebody.keepProjectsProposalsJournalClubsAndStudentWorkAlignedWithTheNewestRelevantPaper", message: "Keep projects, proposals, journal clubs, and student work aligned with the newest relevant papers, datasets, repositories, and patents.", comment: "UI body in components/home/HomePageBody.tsx" }),
    },
    {
        title: msg({ id: "home.homepagebody.rDTeamsAndFounders", message: "R&D teams and founders", comment: "UI title in components/home/HomePageBody.tsx" }),
        body: msg({ id: "home.homepagebody.monitorTechnicalAreasThatCouldAffectRoadmapDecisionsProductDirectionCompetitiveA", message: "Monitor technical areas that could affect roadmap decisions, product direction, competitive analysis, scientific claims, or patent exposure.", comment: "UI body in components/home/HomePageBody.tsx" }),
    },
    {
        title: msg({ id: "home.homepagebody.aiResearchAgents", message: "AI research agents", comment: "UI title in components/home/HomePageBody.tsx" }),
        body: msg({ id: "home.homepagebody.feedAgentsAContinuouslyUpdatedTopicSpecificResearchDataStreamThroughMcpCliOrApiW", message: "Feed agents a continuously updated, topic-specific research-data stream through MCP, CLI, or API workflows, so they start from focused context instead of the open web.", comment: "UI body in components/home/HomePageBody.tsx" }),
    },
];

const TRUST_CARDS = [
    {
        title: msg({ id: "home.homepagebody.sourceLinksFirst", message: "Source links first", comment: "UI title in components/home/HomePageBody.tsx" }),
        body: msg({ id: "home.homepagebody.everyResultPointsBackToTheOriginalPaperDatasetRepositoryOrPatentRecordSoYouCanVe", message: "Every result points back to the original paper, dataset, repository, or patent record, so you can verify claims and inspect the source directly.", comment: "UI body in components/home/HomePageBody.tsx" }),
    },
    {
        title: msg({ id: "home.homepagebody.relevanceBeforeVolume", message: "Relevance before volume", comment: "UI title in components/home/HomePageBody.tsx" }),
        body: msg({ id: "home.homepagebody.paperzillaIsBuiltToReduceAlertNoiseByRankingResearchObjectsAgainstTheSpecificTop", message: "Paperzilla is built to reduce alert noise by ranking research objects against the specific topic you care about.", comment: "UI body in components/home/HomePageBody.tsx" }),
    },
    {
        title: msg({ id: "home.homepagebody.summariesAsStartingPoints", message: "Summaries as starting points", comment: "UI title in components/home/HomePageBody.tsx" }),
        body: (
            <>
                <Link href="/example-summaries" style={{ color: colors.primaryGreenDark, textDecorationLine: 'underline' }}><Trans id="home.homepagebody.aiGeneratedSummaries" comment="UI copy in components/home/HomePageBody.tsx">
                    AI-generated summaries
                </Trans></Link>
                {<Trans id="home.homepagebody.helpWithTriageButTheSourceRecordRemainsTheGround" comment="Conditional UI copy in components/home/HomePageBody.tsx">help with triage, but the source record remains the ground truth.</Trans>}
            </>
        ),
    },
    {
        title: msg({ id: "home.homepagebody.feedbackImprovesTheFeed", message: "Feedback improves the feed", comment: "UI title in components/home/HomePageBody.tsx" }),
        body: msg({ id: "home.homepagebody.yourInteractionsAndFeedbackHelpTheFeedBecomeMoreFocusedOverTime", message: "Your interactions and feedback help the feed become more focused over time.", comment: "UI body in components/home/HomePageBody.tsx" }),
    },
];

export default function HomePageBody({
    digests,
    fontsLoaded,
    onOpenChat,
    targetCount,
}: HomePageBodyProps) {
    const { i18n } = useLingui();
    const { width } = useHydratedWindowDimensions();
    const isNarrow = width < 760;
    const isTablet = width < 980;
    const stepGridStyle = Platform.OS === 'web' && !isTablet
        ? ({ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 24 } as any)
        : null;
    const twoColumnStyle = Platform.OS === 'web' && !isTablet
        ? ({ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 28 } as any)
        : null;
    const fourColumnStyle = Platform.OS === 'web' && !isTablet
        ? ({ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 20 } as any)
        : null;

    return (
        <>
            <HomeResearchBrief fontsLoaded={fontsLoaded} />

            <Section nativeID="system-overview" backgroundColor={colors.bgWeak} top={72} bottom={72} isNarrow={isNarrow}>
                <SectionLabel fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.systemOverview" comment="UI copy in components/home/HomePageBody.tsx">System overview</Trans></SectionLabel>
                <Heading fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.fromResearchDataChaosToFocusedResearchContext" comment="UI copy in components/home/HomePageBody.tsx">
                    From research-data chaos to focused research context
                </Trans></Heading>
                <View style={styles.leadCopy}>
                    <Body fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.paperzillaBringsTogetherPapersDatasetsSoftwareRepositoriesPatentsAndForw" comment="UI copy in components/home/HomePageBody.tsx">
                        Paperzilla brings together papers, datasets, software repositories, patents, and forwarded alerts, filters them against the research topics you care about, and turns them into structured feeds for researchers, teams, tools, and AI agents.
                    </Trans></Body>
                </View>
                <SystemDiagram fontsLoaded={fontsLoaded} includeWorkInProgressItems />
                <Text style={[styles.diagramCaption, fontsLoaded && interRegular]}><Trans id="home.homepagebody.scatteredPapersDatasetsRepositoriesPatentsAndAlertsGoInPaperzilla" comment="UI copy in components/home/HomePageBody.tsx">
                    Scattered papers, datasets, repositories, patents, and alerts go in. Paperzilla filters for relevance. Structured research feeds come out, ready for humans and AI agents. Items marked with * are paper-related datasets, software repos, patents, and API access.
                </Trans></Text>
            </Section>

            <Section nativeID="how-it-works" top={80} bottom={80} isNarrow={isNarrow}>
                <SectionLabel fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.howItWorks" comment="UI copy in components/home/HomePageBody.tsx">How it works</Trans></SectionLabel>
                <Heading fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.howPaperzillaWorks" comment="UI copy in components/home/HomePageBody.tsx">How Paperzilla works</Trans></Heading>
                <View style={styles.leadCopy}>
                    <Body fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.paperzillaContinuouslyMonitorsResearchSourcesFiltersIncomingPapersAndRes" comment="UI copy in components/home/HomePageBody.tsx">
                        Paperzilla continuously monitors research sources, filters incoming papers and research data against your topic, and delivers the results where your research workflow already happens.
                    </Trans></Body>
                </View>
                <View style={[styles.cardGrid, isTablet && styles.stackGrid, stepGridStyle]}>
                    <StepCard fontsLoaded={fontsLoaded} number="01" badge={msg({ id: "home.homepagebody.input", message: "Input", comment: "UI badge in components/home/HomePageBody.tsx" })} title={msg({ id: "home.homepagebody.connectSourcesAndAlerts", message: "Connect sources and alerts", comment: "UI title in components/home/HomePageBody.tsx" })}><Trans id="home.homepagebody.monitorPreprintsAndPubmedIndexedLiteratureBringInPaperRelatedDatasetsSof" comment="UI copy in components/home/HomePageBody.tsx">
                        Monitor preprints and PubMed-indexed literature, bring in paper-related datasets, software repos, and patents, and forward existing alerts into Paperzilla. Bring the scattered streams into one place without rebuilding your monitoring setup from scratch.
                    </Trans></StepCard>
                    <StepCard fontsLoaded={fontsLoaded} number="02" badge={msg({ id: "home.homepagebody.relevance", message: "Relevance", comment: "UI badge in components/home/HomePageBody.tsx" })} title={msg({ id: "home.homepagebody.filterAndRankByRelevance", message: "Filter and rank by relevance", comment: "UI title in components/home/HomePageBody.tsx" })}><Trans id="home.homepagebody.defineTheTopicYouCareAboutPaperzillaRanksIncomingResearch" comment="UI copy in components/home/HomePageBody.tsx">
                        Define the topic you care about. Paperzilla ranks incoming research objects, separates must-reads from background noise, and improves over time with feedback and recommender-style signals.
                    </Trans></StepCard>
                    <StepCard fontsLoaded={fontsLoaded} number="03" badge={msg({ id: "home.homepagebody.output", message: "Output", comment: "UI badge in components/home/HomePageBody.tsx" })} title={msg({ id: "home.homepagebody.sendStructuredOutputAnywhere", message: "Send structured output anywhere", comment: "UI title in components/home/HomePageBody.tsx" })}><Trans id="home.homepagebody.getAMorningBriefFromClaudeChatGptOrAnyMcpAssistant" comment="UI copy in components/home/HomePageBody.tsx. Preserve Claude, ChatGPT, MCP, RSS, CLI, and API.">
                        Get a morning brief from Claude, ChatGPT, or any AI assistant that supports MCP, then ask it about the papers. Or read your feed in the app, by daily or weekly email, or over RSS, and build your own workflows with the CLI and API.
                    </Trans></StepCard>
                </View>
            </Section>

            <HomeSourceSection fontsLoaded={fontsLoaded} />

            <Section top={80} bottom={80} isNarrow={isNarrow}>
                <SectionLabel fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.builtForHumansAndAgents" comment="UI copy in components/home/HomePageBody.tsx">Built for humans and agents</Trans></SectionLabel>
                <Heading fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.builtForResearchersTeamsAndAiAgents" comment="UI copy in components/home/HomePageBody.tsx">
                    Built for researchers, teams, and AI agents
                </Trans></Heading>
                <View style={styles.leadCopy}>
                    <Body fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.paperzillaDoesNotLockYourResearchDataFeedIntoOne" comment="UI copy in components/home/HomePageBody.tsx">
                        Paperzilla does not lock your research data feed into one interface. The same relevance-filtered stream can be read by people, shared with teams, or consumed by AI agents and automation workflows.
                    </Trans></Body>
                </View>
                <View style={[styles.audienceGrid, isTablet && styles.stackGrid, twoColumnStyle]}>
                    {AUDIENCE_COLUMNS.map((column) => (
                        <View key={String(column.title.id)} style={[styles.audienceCard, column.dark && styles.audienceCardDark]}>
                            <Text style={[styles.audienceLabel, column.dark && styles.lightMuted, fontsLoaded && interMedium]}>
                                {i18n._(column.label)}
                            </Text>
                            {column.href ? (
                                <Link
                                    href={column.href}
                                    style={[
                                        styles.audienceTitle,
                                        styles.audienceTitleLink,
                                        column.dark && styles.lightText,
                                        fontsLoaded && interSemiBold,
                                    ]}
                                >
                                    {i18n._(column.title)}
                                </Link>
                            ) : (
                                <Text style={[styles.audienceTitle, column.dark && styles.lightText, fontsLoaded && interSemiBold]}>
                                    {i18n._(column.title)}
                                </Text>
                            )}
                            <Text style={[styles.audienceBody, column.dark && styles.lightBody, fontsLoaded && interRegular]}>
                                {i18n._(column.body)}
                            </Text>
                            <View style={styles.bulletList}>
                                {column.featuredLink ? (
                                    <Link
                                        href={column.featuredLink.href}
                                        style={[styles.bulletText, styles.bulletLink, fontsLoaded && interSemiBold]}
                                    >
                                        {i18n._(column.featuredLink.label)}
                                    </Link>
                                ) : null}
                                {column.bullets.map((bullet) => (
                                    <Text key={String(bullet.id)} style={[styles.bulletText, column.dark && styles.lightBody, fontsLoaded && interRegular]}>
                                        {i18n._(bullet)}
                                    </Text>
                                ))}
                            </View>
                        </View>
                    ))}
                </View>
            </Section>

            <Section backgroundColor={colors.bgWeak} top={80} bottom={80} isNarrow={isNarrow}>
                <SectionLabel fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.useCases" comment="UI copy in components/home/HomePageBody.tsx">Use cases</Trans></SectionLabel>
                <Heading fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.aFocusedFeedForEveryResearchWorkflow" comment="UI copy in components/home/HomePageBody.tsx">
                    A focused feed for every research workflow
                </Trans></Heading>
                <View style={styles.leadCopy}>
                    <Body fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.usePaperzillaAnywhereMissingTheRightPaperDatasetRepositoryOr" comment="UI copy in components/home/HomePageBody.tsx">
                        Use Paperzilla anywhere missing the right paper, dataset, repository, or patent would slow down a project, weaken a decision, or leave an agent working from stale context.
                    </Trans></Body>
                </View>
                <View style={[styles.cardGrid, isTablet && styles.stackGrid, fourColumnStyle]}>
                    {USE_CASES.map((card, index) => (
                        <InfoCard
                            key={String(card.title.id)}
                            body={card.body}
                            fontsLoaded={fontsLoaded}
                            marker={`0${index + 1}`}
                            title={card.title}
                        />
                    ))}
                </View>
            </Section>

            <HomeDigestExamples digests={digests} fontsLoaded={fontsLoaded} targetCount={targetCount} />

            <Section backgroundColor={colors.bgWeak} top={80} bottom={80} isNarrow={isNarrow}>
                <SectionLabel fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.sourceGrounded" comment="UI copy in components/home/HomePageBody.tsx">Source-grounded</Trans></SectionLabel>
                <Heading fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.groundedInSourceDataTunedForRelevance" comment="UI copy in components/home/HomePageBody.tsx">
                    Grounded in source data, tuned for relevance
                </Trans></Heading>
                <View style={styles.leadCopy}>
                    <Body fontsLoaded={fontsLoaded}><Trans id="home.homepagebody.paperzillaIsDesignedToHelpYouDiscoverVerifyAndAct" comment="UI copy in components/home/HomePageBody.tsx">
                        Paperzilla is designed to help you discover, verify, and act on relevant research without treating AI summaries as a replacement for the original source.
                    </Trans></Body>
                </View>
                <View style={[styles.cardGrid, isTablet && styles.stackGrid, fourColumnStyle]}>
                    {TRUST_CARDS.map((card, index) => (
                        <InfoCard
                            key={String(card.title.id)}
                            body={card.body}
                            fontsLoaded={fontsLoaded}
                            marker={`0${index + 1}`}
                            title={card.title}
                        />
                    ))}
                </View>
            </Section>

            <HomeFAQ fontsLoaded={fontsLoaded} />

            <Section nativeID="signup" backgroundColor={colors.primaryGreenDark} top={96} bottom={96} isNarrow={isNarrow}>
                <View style={styles.finalCtaContent}>
                    <SectionLabel fontsLoaded={fontsLoaded} light><Trans id="home.homepagebody.getStarted" comment="UI copy in components/home/HomePageBody.tsx">Get started</Trans></SectionLabel>
                    <Heading fontsLoaded={fontsLoaded} size="medium" light><Trans id="home.homepagebody.turnScatteredResearchDataIntoAFocusedFeed" comment="UI copy in components/home/HomePageBody.tsx">
                        Turn scattered research data into a focused feed
                    </Trans></Heading>
                    <Text style={[styles.finalCtaBody, fontsLoaded && interRegular]}><Trans id="home.homepagebody.tellUsWhatYoureResearchingYourAssistantCanBriefYou" comment="UI copy in components/home/HomePageBody.tsx">
                        Tell us what you're researching. Paperzilla brings relevant papers together in one feed, and your AI assistant can brief you on them every morning.
                    </Trans></Text>
                    <View style={[styles.finalCtaButtons, isNarrow && styles.buttonStack]}>
                        <PrimaryButton fontsLoaded={fontsLoaded} onPress={onOpenChat} style={styles.finalCtaButton}><Trans id="home.homepagebody.createYourResearchFeed" comment="UI copy in components/home/HomePageBody.tsx">
                            Create my research feed
                        </Trans></PrimaryButton>
                    </View>
                    <Text style={[styles.finalCtaNote, fontsLoaded && interRegular]}><Trans id="home.homepagebody.guidedSetupTakesLessThan2Minutes" comment="UI copy in components/home/HomePageBody.tsx">
                        Describe your topic. We'll guide you through setup.
                    </Trans></Text>
                </View>
            </Section>
        </>
    );
}

function InfoCard({
    body,
    fontsLoaded,
    marker,
    title,
}: {
    body: React.ReactNode | MessageDescriptor;
    fontsLoaded: boolean;
    marker: string;
    title: MessageDescriptor;
}) {
    const { i18n } = useLingui();
    const resolvedBody = body && typeof body === 'object' && 'id' in body
        ? i18n._(body as MessageDescriptor)
        : body;
    return (
        <View style={styles.infoCard}>
            <Text style={[styles.infoMarker, fontsLoaded && interMedium]}>{marker}</Text>
            <Text style={[styles.infoTitle, fontsLoaded && interSemiBold]}>{i18n._(title)}</Text>
            <Text style={[styles.infoBody, fontsLoaded && interRegular]}>{resolvedBody}</Text>
        </View>
    );
}

const styles = StyleSheet.create({
    leadCopy: {
        maxWidth: 820,
        marginBottom: 32,
    },
    diagramCaption: {
        color: colors.text600,
        fontSize: 14,
        lineHeight: 22,
        marginTop: 18,
        maxWidth: 760,
    },
    cardGrid: {
        flexDirection: 'row',
        gap: 20,
    },
    stackGrid: {
        flexDirection: 'column',
    },
    audienceGrid: {
        flexDirection: 'row',
        gap: 28,
    },
    audienceCard: {
        backgroundColor: '#ffffff',
        borderColor: colors.border,
        borderRadius: 14,
        borderWidth: 1.5,
        flex: 1,
        paddingHorizontal: 28,
        paddingVertical: 30,
    },
    audienceCardDark: {
        backgroundColor: colors.primaryGreenDark,
        borderColor: colors.primaryGreenDark,
    },
    audienceLabel: {
        color: colors.primaryGreen,
        fontSize: 12,
        fontWeight: '600',
        lineHeight: 17,
        marginBottom: 12,
        textTransform: 'uppercase',
    },
    audienceTitle: {
        color: colors.text900,
        fontSize: 20,
        fontWeight: '700',
        lineHeight: 26,
        marginBottom: 12,
    },
    audienceTitleLink: {
        textDecorationLine: 'underline',
    },
    audienceBody: {
        color: colors.text600,
        fontSize: 15,
        lineHeight: 24,
        marginBottom: 20,
    },
    bulletList: {
        gap: 9,
    },
    bulletText: {
        color: colors.text700,
        fontSize: 14,
        lineHeight: 20,
    },
    bulletLink: {
        color: colors.primaryGreenDark,
        textDecorationLine: 'underline',
    },
    lightText: {
        color: '#ffffff',
    },
    lightBody: {
        color: 'rgba(255,255,255,0.72)',
    },
    lightMuted: {
        color: colors.primaryGreenLight,
    },
    infoCard: {
        backgroundColor: '#ffffff',
        borderColor: colors.border,
        borderRadius: 14,
        borderWidth: 1.5,
        flex: 1,
        paddingHorizontal: 22,
        paddingVertical: 24,
    },
    infoMarker: {
        color: colors.primaryGreen,
        fontSize: 13,
        fontWeight: '600',
        lineHeight: 18,
        marginBottom: 20,
    },
    infoTitle: {
        color: colors.text900,
        fontSize: 16,
        fontWeight: '700',
        lineHeight: 22,
        marginBottom: 10,
    },
    infoBody: {
        color: colors.text600,
        fontSize: 14,
        lineHeight: 23,
    },
    finalCtaContent: {
        alignItems: 'center',
    },
    finalCtaBody: {
        color: 'rgba(255,255,255,0.72)',
        fontSize: 17,
        lineHeight: 28,
        marginTop: 22,
        maxWidth: 760,
        textAlign: 'center',
    },
    finalCtaButtons: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'center',
        marginTop: 34,
    },
    finalCtaButton: {
        alignSelf: 'center',
    },
    buttonStack: {
        alignItems: 'center',
        flexDirection: 'column',
        alignSelf: 'center',
    },
    finalCtaNote: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
        lineHeight: 19,
        marginTop: 18,
        textAlign: 'center',
    },
});
